/**
 * Error Handler Utility
 * 
 * Provides consistent error handling and message extraction across the application.
 * Handles different error types (Axios errors, Error objects, strings, unknown types)
 * and provides user-friendly Turkish error messages.
 */

import axios, { AxiosError } from 'axios'

/**
 * Extract user-friendly error message from various error types
 * 
 * Handles:
 * - AxiosError: Extracts backend error message from response
 * - Error objects: Returns error.message
 * - String errors: Returns the string directly
 * - Unknown types: Returns fallback message
 * 
 * @param error - The error object (can be any type)
 * @param fallbackMessage - Message to return if error cannot be parsed
 * @returns User-friendly error message string
 * 
 * @example
 * try {
 *   await api.post('/endpoint', data)
 * } catch (error) {
 *   const message = handleApiError(error, 'İşlem başarısız')
 *   toast.error(message)
 * }
 */
export function handleApiError(error: unknown, fallbackMessage: string): string {
  // Handle Axios errors (most common case - API calls)
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<any>
    
    // Try to extract error message from various response formats
    // Backend can return: { message }, { error }, or plain string
    const responseData = axiosError.response?.data
    
    if (responseData) {
      // If response data is a plain string, return it
      if (typeof responseData === 'string') {
        return responseData
      }
      
      // If response data is an object, try common error message fields
      if (typeof responseData === 'object') {
        // Try 'message' field first (most common)
        if (responseData.message && typeof responseData.message === 'string') {
          return responseData.message
        }
        
        // Try 'error' field
        if (responseData.error && typeof responseData.error === 'string') {
          return responseData.error
        }
        
        // Try 'title' field (sometimes used in problem details)
        if (responseData.title && typeof responseData.title === 'string') {
          return responseData.title
        }
      }
    }
    
    // If no response data, use axios error message
    if (axiosError.message) {
      return axiosError.message
    }
  }
  
  // Handle standard Error objects
  if (error instanceof Error) {
    return error.message
  }
  
  // Handle string errors
  if (typeof error === 'string') {
    return error
  }
  
  // Handle custom error objects with message property
  if (error && typeof error === 'object' && 'message' in error) {
    const errorObj = error as { message: unknown }
    if (typeof errorObj.message === 'string') {
      return errorObj.message
    }
  }
  
  // Fallback: return the provided fallback message
  return fallbackMessage
}

/**
 * Check if error is an authentication error (401 Unauthorized)
 * Useful for triggering logout or token refresh
 * 
 * @param error - The error object to check
 * @returns true if error is 401 Unauthorized
 */
export function isAuthError(error: unknown): boolean {
  if (axios.isAxiosError(error)) {
    return error.response?.status === 401
  }
  return false
}

/**
 * Check if error is a network error (no response from server)
 * Useful for showing "check your connection" messages
 * 
 * @param error - The error object to check
 * @returns true if error is a network error
 */
export function isNetworkError(error: unknown): boolean {
  if (axios.isAxiosError(error)) {
    return !error.response && error.message.toLowerCase().includes('network')
  }
  return false
}

/**
 * Check if error is a validation error (400 Bad Request)
 * Useful for handling form validation errors
 * 
 * @param error - The error object to check
 * @returns true if error is 400 Bad Request
 */
export function isValidationError(error: unknown): boolean {
  if (axios.isAxiosError(error)) {
    return error.response?.status === 400
  }
  return false
}

/**
 * Check if error is a server error (5xx status codes)
 * Useful for showing "try again later" messages
 * 
 * @param error - The error object to check
 * @returns true if error is a 5xx server error
 */
export function isServerError(error: unknown): boolean {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status
    return !!status && status >= 500 && status < 600
  }
  return false
}

/**
 * Extract validation errors from backend response
 * Many .NET APIs return validation errors in a specific format
 * 
 * @param error - The error object
 * @returns Array of validation error messages, or null if not a validation error
 */
export function extractValidationErrors(error: unknown): string[] | null {
  if (!axios.isAxiosError(error)) {
    return null
  }
  
  const responseData = error.response?.data
  
  if (!responseData || typeof responseData !== 'object') {
    return null
  }
  
  // Handle ASP.NET Core validation problem details format
  if ('errors' in responseData && typeof responseData.errors === 'object') {
    const errors: string[] = []
    const errorObj = responseData.errors as Record<string, string[]>
    
    for (const field in errorObj) {
      const fieldErrors = errorObj[field]
      if (Array.isArray(fieldErrors)) {
        errors.push(...fieldErrors)
      }
    }
    
    return errors.length > 0 ? errors : null
  }
  
  return null
}

/**
 * Create a standardized error object for throwing
 * Ensures consistent error format across the application
 * 
 * @param error - The caught error
 * @param fallbackMessage - Message to use if error cannot be parsed
 * @returns Error object ready to be thrown
 */
export function createError(error: unknown, fallbackMessage: string): Error {
  const message = handleApiError(error, fallbackMessage)
  return new Error(message)
}
